import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position, useNodes } from '@xyflow/react';
import { 
  message, Alert, Card, Select, Button, Input, Space, 
  Typography, Tooltip, InputNumber, Tabs, Tag, Spin, 
  Upload, Popconfirm, Badge, Divider 
} from 'antd';
import { 
  DeleteOutlined, 
  InfoCircleOutlined, 
  VideoCameraOutlined,
  PictureOutlined,
  UploadOutlined,
  DollarOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import debounce from 'lodash/debounce';
import GenericNodeContent from '../common/GenericNodeContent';
import NodeInputHandler from '../common/NodeInputHandler';
import axios from 'axios';
import { adminInstance } from '../api/axiosConfig';
import config from '../../config';

const { Option } = Select;
const { Text, Title } = Typography;
const { TabPane } = Tabs;

const MEDIA_TYPES = {
  TEXT_TO_IMAGE: 'textToImage',
  IMAGE_TO_VIDEO: 'imageToVideo',
  TEXT_TO_VIDEO: 'textToVideo'
};

/**
 * MultimediaGeneratorNode Component
 * 
 * A node for generating images and videos using Fal.ai models
 */
const MultimediaGeneratorNode = ({ id, data, isConnectable }) => {
  // Basic node state
  const [nodeName, setNodeName] = useState(data.nodeName || `Multimedia Generator ${id}`);
  const [activeTab, setActiveTab] = useState(data.modelType || MEDIA_TYPES.TEXT_TO_IMAGE);
  
  // Model selection
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsFetching, setModelsFetching] = useState(false);
  const [selectedModel, setSelectedModel] = useState(data.selectedModel || '');
  
  // Common parameters
  const [prompt, setPrompt] = useState(data.prompt || '');
  const [negativePrompt, setNegativePrompt] = useState(data.negativePrompt || '');
  const [estimatedCost, setEstimatedCost] = useState(null);
  
  // Image to Video parameters
  const [imageUrl, setImageUrl] = useState(data.imageUrl || '');
  const [uploadLoading, setUploadLoading] = useState(false);
  
  // Common video parameters
  const [aspectRatio, setAspectRatio] = useState(data.aspectRatio || '16:9');
  const [videoLength, setVideoLength] = useState(data.videoLength || 5);
  
  // Result and status
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(data.result || null);
  const [error, setError] = useState(null);
  const [hasApiKey, setHasApiKey] = useState(false);
  
  const nodes = useNodes();

  // Fetch available models on component mount
  useEffect(() => {
    checkApiKey();
    fetchModels();
  }, []);

  // Update node data when relevant state changes
  useEffect(() => {
    data.updateNodeData(id, { 
      nodeName,
      modelType: activeTab,
      selectedModel,
      imageUrl,
      prompt,
      negativePrompt,
      aspectRatio,
      result,
      videoLength
    });
  }, [
    nodeName, activeTab, selectedModel, imageUrl, prompt, 
    negativePrompt, aspectRatio, result, videoLength, id, data
  ]);

  // Get estimated cost when model or relevant params change
  useEffect(() => {
    if (selectedModel) {
      estimateCost();
    }
  }, [selectedModel, videoLength, activeTab]);

  // GenericNodeContent handlers
  const handlePromptChange = (nodeId, newData) => {
    setPrompt(newData.content);
    data.updateNodeData(nodeId, { prompt: newData.content });
  };

  const handleNegativePromptChange = (nodeId, newData) => {
    setNegativePrompt(newData.content);
    data.updateNodeData(nodeId, { negativePrompt: newData.content });
  };

  // Check if Fal.ai API key is configured
  const checkApiKey = async () => {
    try {
      const response = await adminInstance.get('/settings');
      setHasApiKey(!!response.data.fal_api_key);
    } catch (error) {
      console.error('Error checking API key:', error);
      setHasApiKey(false);
    }
  };

  // Fetch available models from the backend
  const fetchModels = async () => {
    setModelsFetching(true);
    try {
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/multimedia/models`,
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      
      if (response.data && Array.isArray(response.data)) {
        setAvailableModels(response.data);
      }
    } catch (error) {
      console.error('Error fetching models:', error);
      message.error('Failed to fetch available models');
    } finally {
      setModelsFetching(false);
    }
  };

  // Get estimated cost for the current configuration
  const estimateCost = debounce(async () => {
    if (!selectedModel) return;
    
    try {
      const quantity = activeTab === MEDIA_TYPES.TEXT_TO_IMAGE ? 1 : videoLength;
      
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/multimedia/estimate-cost`,
        {
          model: selectedModel,
          type: activeTab,
          quantity
        },
        { headers: { 'X-WP-Nonce': config.apiSettings.nonce } }
      );
      
      if (response.data && response.data.formatted_cost) {
        setEstimatedCost(response.data.formatted_cost);
      }
    } catch (error) {
      console.error('Error estimating cost:', error);
      setEstimatedCost(null);
    }
  }, 500);

  // Handle file upload for image-to-video
  const handleUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    setUploadLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/upload-file`,
        formData,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (response.data && response.data.url) {
        setImageUrl(response.data.url);
        message.success(`${file.name} uploaded successfully`);
        onSuccess(response, file);
      } else {
        message.error(`${file.name} upload failed.`);
        onError(new Error('Upload failed'));
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      message.error(`${file.name} upload failed.`);
      onError(error);
    } finally {
      setUploadLoading(false);
    }
  };

  // Validate file upload requirements
  const beforeUpload = (file) => {
    const isValidImageType = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type);
    
    if (!isValidImageType) {
      message.error('You can only upload JPG/PNG/GIF/WEBP files!');
      return Upload.LIST_IGNORE;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Image must be smaller than 5MB!');
      return Upload.LIST_IGNORE;
    }
    
    return true;
  };

  // Generate media based on active tab
  const generateMedia = async () => {
    if (!hasApiKey) {
      message.error('Please add your Fal.ai API key in settings first');
      return;
    }
    
    if (!selectedModel) {
      message.error('Please select a model');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    let apiEndpoint = '';
    let payload = {};
    
    // Build the request based on active tab (media type)
    switch (activeTab) {
      case MEDIA_TYPES.TEXT_TO_IMAGE:
        if (!prompt) {
          message.error('Please provide a prompt');
          setLoading(false);
          return;
        }
        
        apiEndpoint = 'generate-image';
        payload = {
          model: selectedModel,
          prompt,
          negative_prompt: negativePrompt
        };
        break;
        
      case MEDIA_TYPES.IMAGE_TO_VIDEO:
        if (!imageUrl) {
          message.error('Please provide an image URL');
          setLoading(false);
          return;
        }
        
        apiEndpoint = 'generate-video';
        payload = {
          model: selectedModel,
          image_url: imageUrl,
          prompt,
          video_length: videoLength,
          aspect_ratio: aspectRatio
        };
        break;
        
      case MEDIA_TYPES.TEXT_TO_VIDEO:
        if (!prompt) {
          message.error('Please provide a prompt');
          setLoading(false);
          return;
        }
        
        apiEndpoint = 'generate-video';
        payload = {
          model: selectedModel,
          prompt,
          video_length: videoLength,
          aspect_ratio: aspectRatio
        };
        break;
        
      default:
        message.error('Invalid media type');
        setLoading(false);
        return;
    }
    
    try {
      const response = await axios.post(
        `${config.apiSettings.root}wp-ai-workflows/v1/${apiEndpoint}`,
        payload,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      if (response.data && response.data.result) {
        setResult(response.data.result);
        message.success('Media generated successfully');
      } else {
        setError('Unexpected response format');
        message.error('Failed to generate media');
      }
    } catch (error) {
      console.error('Error generating media:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      setError(errorMessage);
      message.error(`Failed to generate media: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle image tag insertion for input handlers
  const handleInsertImageTag = useCallback((tag) => {
    setImageUrl(prevUrl => prevUrl ? prevUrl + tag : tag);
  }, []);

  // Filter models based on active tab
  const getFilteredModels = () => {
    if (!availableModels || !availableModels.length) return [];
    return availableModels.filter(model => model.type === activeTab);
  };

  /**
   * Tab content for Text to Image generation
   */
  const renderTextToImageTab = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>Model</Text>
          <Select
            loading={modelsFetching}
            className="nodrag"
            style={{ width: '100%', marginTop: 4 }}
            value={selectedModel}
            onChange={setSelectedModel}
            placeholder={modelsFetching ? "Loading models..." : "Select a model"}
            notFoundContent={modelsFetching ? <Spin size="small" /> : "No models available"}
          >
            {getFilteredModels().map(model => (
              <Option key={model.id} value={model.id}>
                <Space align="center">
                  <PictureOutlined />
                  <span>{model.name}</span>
                  {model.cost_label && <Tag color="blue">{model.cost_label}</Tag>}
                </Space>
              </Option>
            ))}
          </Select>
        </div>
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>Prompt</Text>
        <GenericNodeContent
          id={id}
          data={{ content: prompt }}
          onChange={handlePromptChange}
          placeholder="Describe the image you want to generate"
          rows={4}
          nodes={nodes}
        />
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>Negative Prompt</Text>
        <GenericNodeContent
          id={id}
          data={{ content: negativePrompt }}
          onChange={handleNegativePromptChange}
          placeholder="What to avoid in the image (optional)"
          rows={2}
          nodes={nodes}
        />
      </div>
      
      {estimatedCost && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              <Space>
                <DollarOutlined />
                <Text>Estimated cost: {estimatedCost}</Text>
              </Space>
            }
            type="info"
            showIcon={false}
          />
        </div>
      )}
      
      <Button
        type="primary"
        onClick={generateMedia}
        loading={loading}
        disabled={!selectedModel || !hasApiKey || !prompt}
        block
        className="nodrag"
      >
        Generate Image
      </Button>
      
      {renderResult()}
    </Space>
  );

  /**
   * Tab content for Image to Video generation
   */
  const renderImageToVideoTab = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>Model</Text>
          <Select
            loading={modelsFetching}
            className="nodrag"
            style={{ width: '100%', marginTop: 4 }}
            value={selectedModel}
            onChange={setSelectedModel}
            placeholder={modelsFetching ? "Loading models..." : "Select a model"}
            notFoundContent={modelsFetching ? <Spin size="small" /> : "No models available"}
          >
            {getFilteredModels().map(model => (
              <Option key={model.id} value={model.id}>
                <Space align="center">
                  <VideoCameraOutlined />
                  <span>{model.name}</span>
                  {model.cost_label && <Tag color="orange">{model.cost_label}</Tag>}
                </Space>
              </Option>
            ))}
          </Select>
        </div>
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>Input Image</Text>
        <div style={{ marginBottom: 8 }}>
          <Input
            value={imageUrl}
            onChange={e => setImageUrl(e.target.value)}
            placeholder="Enter image URL or upload an image"
            className="nodrag"
            style={{ marginBottom: 8 }}
            suffix={
              <Tooltip title="Use a URL from a previous node">
                <NodeInputHandler
                  nodeId={id}
                  onInsert={handleInsertImageTag}
                />
              </Tooltip>
            }
          />
          <Upload
            customRequest={handleUpload}
            beforeUpload={beforeUpload}
            showUploadList={false}
          >
            <Button
              icon={<UploadOutlined />}
              loading={uploadLoading}
              className="nodrag"
              style={{ marginTop: 8 }}
            >
              Upload Image
            </Button>
          </Upload>
        </div>
        {imageUrl && (
          <div style={{ marginTop: 8, marginBottom: 16 }}>
            <img
              src={imageUrl}
              alt="Source"
              style={{ 
                maxWidth: '100%', 
                maxHeight: 200, 
                objectFit: 'contain',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                padding: '4px'
              }}
            />
          </div>
        )}
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>Motion Prompt</Text>
        <GenericNodeContent
          id={id}
          data={{ content: prompt }}
          onChange={handlePromptChange}
          placeholder="Describe how the image should animate"
          rows={2}
          nodes={nodes}
        />
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Video Settings</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Text>Duration (Seconds)</Text>
              <Select
                value={videoLength}
                onChange={setVideoLength}
                className="nodrag"
                style={{ width: '100%' }}
              >
                <Option value={5}>5 seconds</Option>
                <Option value={6}>6 seconds</Option>
                <Option value={7}>7 seconds</Option>
                <Option value={8}>8 seconds</Option>
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Text>Aspect Ratio</Text>
              <Select
                value={aspectRatio}
                onChange={setAspectRatio}
                className="nodrag"
                style={{ width: '100%' }}
              >
                <Option value="16:9">16:9 (Landscape)</Option>
                <Option value="9:16">9:16 (Portrait)</Option>
                <Option value="1:1">1:1 (Square)</Option>
                <Option value="auto">Auto (from image)</Option>
              </Select>
            </div>
          </div>
        </Space>
      </div>
      
      {estimatedCost && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              <Space>
                <DollarOutlined />
                <Text>Estimated cost: {estimatedCost}</Text>
              </Space>
            }
            type="info"
            showIcon={false}
          />
        </div>
      )}
      
      <Button
        type="primary"
        onClick={generateMedia}
        loading={loading}
        disabled={!selectedModel || !hasApiKey || !imageUrl}
        block
        className="nodrag"
      >
        Generate Video
      </Button>
      
      {renderResult()}
    </Space>
  );

  /**
   * Tab content for Text to Video generation
   */
  const renderTextToVideoTab = () => (
    <Space direction="vertical" style={{ width: '100%' }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>Model</Text>
          <Select
            loading={modelsFetching}
            className="nodrag"
            style={{ width: '100%', marginTop: 4 }}
            value={selectedModel}
            onChange={setSelectedModel}
            placeholder={modelsFetching ? "Loading models..." : "Select a model"}
            notFoundContent={modelsFetching ? <Spin size="small" /> : "No models available"}
          >
            {getFilteredModels().map(model => (
              <Option key={model.id} value={model.id}>
                <Space align="center">
                  <VideoCameraOutlined />
                  <span>{model.name}</span>
                  {model.cost_label && <Tag color="purple">{model.cost_label}</Tag>}
                </Space>
              </Option>
            ))}
          </Select>
        </div>
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>Prompt</Text>
        <GenericNodeContent
          id={id}
          data={{ content: prompt }}
          onChange={handlePromptChange}
          placeholder="Describe the video you want to generate"
          rows={4}
          nodes={nodes}
        />
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Text strong>Video Settings</Text>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <Text>Duration (Seconds)</Text>
              <Select
                value={videoLength}
                onChange={setVideoLength}
                className="nodrag"
                style={{ width: '100%' }}
              >
                <Option value={5}>5 seconds</Option>
                <Option value={6}>6 seconds</Option>
                <Option value={7}>7 seconds</Option>
                <Option value={8}>8 seconds</Option>
                {selectedModel === 'fal-ai/kling-video/v2/master/text-to-video' && (
                  <Option value={10}>10 seconds</Option>
                )}
              </Select>
            </div>
            <div style={{ flex: 1 }}>
              <Text>Aspect Ratio</Text>
              <Select
                value={aspectRatio}
                onChange={setAspectRatio}
                className="nodrag"
                style={{ width: '100%' }}
              >
                <Option value="16:9">16:9 (Landscape)</Option>
                <Option value="9:16">9:16 (Portrait)</Option>
                {selectedModel === 'fal-ai/kling-video/v2/master/text-to-video' && (
                  <Option value="1:1">1:1 (Square)</Option>
                )}
              </Select>
            </div>
          </div>
        </Space>
      </div>
      
      {estimatedCost && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={
              <Space>
                <DollarOutlined />
                <Text>Estimated cost: {estimatedCost}</Text>
              </Space>
            }
            type="info"
            showIcon={false}
          />
        </div>
      )}
      
      <Button
        type="primary"
        onClick={generateMedia}
        loading={loading}
        disabled={!selectedModel || !hasApiKey || !prompt}
        block
        className="nodrag"
      >
        Generate Video
      </Button>
      
      {renderResult()}
    </Space>
  );

  /**
   * Render generation results
   */
  const renderResult = () => {
    if (!result) return null;
    
    if (error) {
      return (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      );
    }
    
    switch (activeTab) {
      case MEDIA_TYPES.TEXT_TO_IMAGE:
        return (
          <div style={{ marginTop: 16 }}>
            <Divider>
              <Badge status="success" text="Generated Image" />
            </Divider>
            <div style={{ marginTop: 8 }}>
              {result.images && result.images.map((image, index) => (
                <div key={index} style={{ marginBottom: 16 }}>
                  <img
                    src={image.url}
                    alt={`Generated ${index + 1}`}
                    style={{ 
                      width: '100%', 
                      maxHeight: 300, 
                      objectFit: 'contain',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      padding: '4px' 
                    }}
                  />
                  <Space style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      onClick={() => window.open(image.url, '_blank')}
                      icon={<GlobalOutlined />}
                    >
                      Open
                    </Button>
                    <Text type="secondary">
                      {image.width} × {image.height}
                    </Text>
                  </Space>
                </div>
              ))}
            </div>
          </div>
        );
        
      case MEDIA_TYPES.IMAGE_TO_VIDEO:
      case MEDIA_TYPES.TEXT_TO_VIDEO:
        return (
          <div style={{ marginTop: 16 }}>
            <Divider>
              <Badge status="success" text="Generated Video" />
            </Divider>
            <div style={{ marginTop: 8 }}>
              {result.video && (
                <div>
                  <video
                    controls
                    loop
                    preload="metadata"
                    style={{ 
                      width: '100%', 
                      maxHeight: 300,
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      backgroundColor: '#000'
                    }}
                  >
                    <source src={result.video.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <Space style={{ marginTop: 8 }}>
                    <Button
                      type="link"
                      onClick={() => window.open(result.video.url, '_blank')}
                      icon={<GlobalOutlined />}
                    >
                      Open
                    </Button>
                  </Space>
                </div>
              )}
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };

  // Render media generation warning if API key is missing
  const renderApiKeyWarning = () => {
    if (!hasApiKey) {
      return (
        <Alert
          message="API Key Required"
          description={
            <div>
              <p>Please add your Fal.ai API key in the plugin settings.</p>
              <a href="https://fal.ai/dashboard/keys" target="_blank" rel="noopener noreferrer">
                Get your API key here
              </a>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      );
    }
    return null;
  };

  // Icon for each tab based on media type
  const getTabIcon = (type) => {
    switch (type) {
      case MEDIA_TYPES.TEXT_TO_IMAGE:
        return <PictureOutlined />;
      case MEDIA_TYPES.IMAGE_TO_VIDEO:
      case MEDIA_TYPES.TEXT_TO_VIDEO:
        return <VideoCameraOutlined />;
      default:
        return null;
    }
  };

  return (
    <Card 
      title={
        <Space>
          {activeTab === MEDIA_TYPES.TEXT_TO_IMAGE ? (
            <PictureOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
          ) : (
            <VideoCameraOutlined style={{ fontSize: '18px', color: '#1890ff' }}/>
          )}
          <span>Multimedia Generator</span>
          {selectedModel && getFilteredModels().length > 0 && (
            <Tag color="blue">
              {getFilteredModels().find(m => m.id === selectedModel)?.name || selectedModel}
            </Tag>
          )}
        </Space>
      }
      style={{ width: 450 }}
      extra={
        <Popconfirm
          title="Delete Node"
          description="Are you sure you want to delete this node?"
          onConfirm={() => data.onDelete(id)}
          okText="Delete"
          cancelText="Cancel"
          okButtonProps={{ danger: true }}
        >
          <Button icon={<DeleteOutlined />} type="text" />
        </Popconfirm>
      }
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />
      
      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={e => setNodeName(e.target.value)}
        style={{ marginBottom: 16 }}
        prefix={<InfoCircleOutlined style={{ color: '#bfbfbf' }} />}
        className="nodrag"
      />

      {renderApiKeyWarning()}

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane 
          tab={
            <span>
              {getTabIcon(MEDIA_TYPES.TEXT_TO_IMAGE)}
              Text to Image
            </span>
          } 
          key={MEDIA_TYPES.TEXT_TO_IMAGE}
        >
          {renderTextToImageTab()}
        </TabPane>
        <TabPane 
          tab={
            <span>
              {getTabIcon(MEDIA_TYPES.IMAGE_TO_VIDEO)}
              Image to Video
            </span>
          } 
          key={MEDIA_TYPES.IMAGE_TO_VIDEO}
        >
          {renderImageToVideoTab()}
        </TabPane>
        <TabPane 
          tab={
            <span>
              {getTabIcon(MEDIA_TYPES.TEXT_TO_VIDEO)}
              Text to Video
            </span>
          } 
          key={MEDIA_TYPES.TEXT_TO_VIDEO}
        >
          {renderTextToVideoTab()}
        </TabPane>
      </Tabs>

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default MultimediaGeneratorNode;